import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RunInstrument } from './useRunInstruments';

type UseFollowRobotParams = {
  instruments: RunInstrument[];
  activeCell: string | null;
  isRunning: boolean;
  selectedInstrumentId: string | null;
  onFollowSelect: (woDetailGlobalId: string) => void;
};

type UseFollowRobotResult = {
  following: boolean;
  activeInstrumentId: string | null;
  pauseFollowing: () => void;
  resumeFollowing: () => void;
};

export const useFollowRobot = ({
  instruments,
  activeCell,
  isRunning,
  selectedInstrumentId,
  onFollowSelect
}: UseFollowRobotParams): UseFollowRobotResult => {
  const [following, setFollowing] = useState(true);
  const wasRunningRef = useRef(false);

  const activeInstrumentId = useMemo(() => {
    if (!activeCell) return null;
    return (
      instruments.find(instrument => instrument.cell === activeCell)
        ?.woDetailGlobalId ?? null
    );
  }, [activeCell, instruments]);

  useEffect(() => {
    if (isRunning && !wasRunningRef.current) {
      setFollowing(true);
    } else if (!isRunning && wasRunningRef.current) {
      setFollowing(false);
    }
    wasRunningRef.current = isRunning;
  }, [isRunning]);

  useEffect(() => {
    if (!following || !activeInstrumentId) return;
    if (activeInstrumentId === selectedInstrumentId) return;
    onFollowSelect(activeInstrumentId);
  }, [following, activeInstrumentId, selectedInstrumentId, onFollowSelect]);

  const pauseFollowing = useCallback(() => setFollowing(false), []);

  const resumeFollowing = useCallback(() => {
    setFollowing(true);
    if (activeInstrumentId) onFollowSelect(activeInstrumentId);
  }, [activeInstrumentId, onFollowSelect]);

  return { following, activeInstrumentId, pauseFollowing, resumeFollowing };
};
